import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import { useTranslation } from 'react-i18next';
import { colors, spacing, typography, radius } from '../../theme/tokens';

const BannerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  padding: `${spacing.xs} ${spacing.sm}`,
  backgroundColor: colors.neutral[50],
  border: `1px solid ${colors.border.light}`,
  borderRadius: radius.card,
  marginBottom: spacing.sm,
  flexWrap: 'nowrap',
  justifyContent: 'center',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: `4px ${spacing.xs}`,
    gap: '4px',
    marginBottom: spacing.xs,
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: colors.neutral[200],
  color: colors.text.secondary,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    width: '20px',
    height: '20px',
  },
  '& svg': {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
    },
  },
}));

const TrustItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  [theme.breakpoints.down('sm')]: {
    gap: '4px',
  },
}));

const TrustText = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize.xs,
  fontWeight: typography.fontWeight.regular,
  color: colors.text.secondary,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    display: 'none',
  },
}));

const Divider = styled(Box)(({ theme }) => ({
  width: '1px',
  height: '24px',
  backgroundColor: colors.info.main,
  opacity: 0.3,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

/**
 * Trust banner component showing security and privacy indicators
 */
export default function TrustBanner() {
  const { t } = useTranslation();

  const trustItems = [
    {
      icon: <LockIcon />,
      text: t('trustBanner.encryption', 'הצפנת SSL מאובטחת'),
    },
    {
      icon: <VerifiedUserIcon />,
      text: t('trustBanner.privacy', 'הפרטיות שלך מוגנת'),
    },
    {
      icon: <PrivacyTipIcon />,
      text: t('trustBanner.secure', 'נתונים מאובטחים'),
    },
  ];

  return (
    <BannerContainer>
      {trustItems.map((item, index) => (
        <React.Fragment key={index}>
          <TrustItem>
            <IconWrapper>{item.icon}</IconWrapper>
            <TrustText>{item.text}</TrustText>
          </TrustItem>
          {index < trustItems.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </BannerContainer>
  );
}
